/* -------------------------- Design imports start -------------------------- */
import { Box, IconButton, Popover, Stack } from '@mui/material'
import { Wheel, ShadeSlider } from '@uiw/react-color'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import { validHex, color as createColor, type ColorResult } from '@uiw/react-color'
import { type KeyboardEvent, type MouseEvent, type ReactElement, useState } from 'react'
import TextInput from './TextInput'
import { SettingsBackupRestoreRounded } from '@mui/icons-material'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  id?: string // input id attribute
  name?: string // input name attribute
  defaultColor?: string
  onChange: (hexValue: string) => void
  onColorSquareClick?: (hexValue: string) => void
  disabled?: boolean
}
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
/**
 * A color input component that enables the user to select a color from a color
 * wheel and/or choose a color by inputting a hex value.
 * @param props
 * @constructor
 */
export default function ColorInput(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const { onChange, onColorSquareClick, defaultColor = '#ffffff', disabled } = props

  // validate default color
  if (!validHex(defaultColor)) {
    throw new Error(
      `${defaultColor} is not a valid hex color value. Please provide a ` +
        'valid hex color value as defaultColor prop.'
    )
  }
  /* --------------------------- Non state data end --------------------------- */

  /* --------------------------- Data states start -------------------------- */
  const [color, setColor] = useState<ColorResult>(createColor(defaultColor))
  // We need to temporarily store the input value of the Hex input field, because
  // writing an invalid hex value into the color state breaks the color wheel.
  const [tempHexValue, setTempHexValue] = useState<string | null>(null)
  // anchor element for the color-wheel popover
  const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null)
  /* ---------------------------- Data states end --------------------------- */

  /* ----------------------- Callback functions start ----------------------- */
  const handleColorSquareClick = (event: MouseEvent<HTMLSpanElement>): void => {
    if (disabled) return
    // open popover
    setAnchorEl(event.currentTarget)

    // execute callback
    onColorSquareClick?.(color.hex)
  }
  const handleColorSquareKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
    if (disabled) return
    if (event.key === 'Enter') {
      // open popover on enter
      setAnchorEl(event.currentTarget)
    }

    // execute callback
    onColorSquareClick?.(color.hex)
  }
  const handleClose = (): void => {
    // close popover
    setAnchorEl(null)
  }
  const handleColorChange = (color: ColorResult): void => {
    setColor(color)
    onChange(color.hex)
  }
  /* ------------------------ Callback functions end ------------------------ */

  /* ------------------------ Render constants start ------------------------ */
  const open = Boolean(anchorEl)
  const id = open ? 'color-popover' : undefined
  /* ------------------------- Render constants end ------------------------- */

  /* ------------------------ Callback functions end ------------------------ */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <Box
        component="span"
        onClick={handleColorSquareClick}
        onKeyDown={handleColorSquareKeyDown}
        tabIndex={disabled ? -1 : 0}
        sx={{
          width: 40,
          height: 40,
          borderRadius: '4px',
          backgroundColor: color.hex,
          display: 'inline-block',
          cursor: disabled ? 'unset' : 'pointer',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          position: 'relative',
        }}
      >
        <input
          type="color"
          value={color.hex}
          onClick={e => e.preventDefault()}
          style={{
            opacity: 0,
            position: 'absolute',
            width: '100%',
            height: '100%',
            cursor: 'inherit',
          }}
          readOnly
          disabled={disabled}
        />
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box p={2} display="flex" flexDirection="column" gap={2} alignItems="center">
          <Wheel color={color.hsva} radius={100} onChange={handleColorChange} />
          <ShadeSlider
            hsva={color.hsva}
            onChange={newShade => {
              const newColor = createColor({ ...color.hsva, ...newShade })
              handleColorChange(newColor)
            }}
            style={{ width: '100%' }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <TextInput
              value={tempHexValue !== null ? tempHexValue : color.hex}
              label={'Hex'}
              onChange={value => {
                if (validHex(value) && value.length >= 6) {
                  // the user input a valid hex value -> update the color state
                  const newColor = createColor(value)
                  handleColorChange(newColor)
                  // and reset the temporary hex input value
                  setTempHexValue(null)
                } else setTempHexValue(value)
              }}
            />
            <IconButton
              onClick={() => handleColorChange(createColor(defaultColor))}
              sx={{
                width: 'max-content',
                height: 'max-content',
              }}
            >
              <SettingsBackupRestoreRounded />
            </IconButton>
          </Stack>
        </Box>
      </Popover>
    </>
  )
}
